"use client";

import { useEffect, useMemo, useState } from "react";
import L, { Browser } from "leaflet";
import {
	MapContainer,
	Marker,
	TileLayer,
	Tooltip,
	useMap,
	useMapEvents,
} from "react-leaflet";
import useDebounce from "@/hooks/useDebounce";
import { trackStepOnMapClick } from "@/utils/mixpanel";
import MarkerClusterGroup from "react-leaflet-markercluster";
import classNames from "classnames";

import "leaflet/dist/leaflet.css";
import styles from "./LeafletMapStyles.module.scss";
import { GlutenFreeIcons } from "@/constants/gluten-free";

let isStepClicked = false;
let allowClick = true;

const Config = {
	user_name: "stepsprod",
	style_id: "ckncu0enk1tdx17n0ruk0rz5k",
	new_style_id: "clluv246y00d601qy1uf3guav",
	access_token:
		"pk.eyJ1Ijoic3RlcHNwcm9kIiwiYSI6ImNrbW5va2Y0bTB5eGEycXJ6aTVnYnh2aXMifQ.ybxcnXuaQ4WB00KuJdvuWg",
};

export const stepIcon = {
	selectedStep: {
		key: "selectedStepIcon",
		src: "/images/MarkerSelected.svg",
		size: [36, 36],
	},
	ghostStep: {
		key: "ghostStepIcon",
		src: "/images/top-ten/ghost-step-marker.svg",
	},
	step: {
		key: "stepIcon",
		src: "/images/Marker.svg",
		size: [36, 36],
	},
	archived: {
		key: "archivedStepIcon",
		src: "/images/top-ten/archived-step-marker.svg",
	},
	selectedArchivedStep: {
		key: "selectedArchivedStepIcon",
		src: "/images/top-ten/selected-archived-step-marker.svg",
	},
};

const createClusterCustomIcon = function (cluster) {
	const markers = cluster.getAllChildMarkers();
	let markerWithMostRecs = {
		total_recommendations: 0,
	};

	markers.forEach((item) => {
		if (
			item.options.place.total_recommendations >
			markerWithMostRecs.total_recommendations
		) {
			markerWithMostRecs = item.options.place;
		}
	});

	const iconUrl = markerWithMostRecs.matchedMarker.getSrc(
		markerWithMostRecs.icon,
		markerWithMostRecs.total_recommendations || 0
	);

	const icon = L.divIcon({
		className: "custom-cluster-icon",
		html: `
          <div class="request-popup">
            <div class="${styles.clusterMarkerTitle}">
                ${markerWithMostRecs.title}
            </div>
            <img src="${iconUrl}" alt="${markerWithMostRecs.title}"/>
          </div>
        `,
	});

	// return L.icon({
	//     name: 'clusterIcon',
	//     title: 'foo',
	//     tooltipAnchor: [0, 0],
	//     iconUrl: iconUrl,
	// })
	return icon;
};

const MapSTPS = ({
	center,
	zoom,
	steps = [],
	flyToPosition,
	selectedStepsIds = [],
	isFitBoundsBySteps,
	onClickMap,
	onClickStep,
	onZoomStart,
	onZoomEnd,
	onDragEnd,
	onDragStart,
	zoomControl,
	updateMapCenter,
	scrollWheelZoom,
	children,
	marker = stepIcon.step,
	markers = [],
	disableZoomEvents = false,
	enforceStaticMap = false,
	showAllTooltips = false,
	isClusterMap = false,
	withMapEvents = true,
	isMobile = false,
}) => {
	const [hoveredStep, setHoveredStep] = useState(null);
	const [selectedStepId, setSelectedStepId] = useState(steps[0]?._id);

	const createStepIcon = (type) => {
		return L.icon({
			iconUrl: type.src,
			iconSize: type.size,
		});
	};

	const markerEventHandlers = (step) => ({
		click: () => {
			handleStepOnMapClick(step);
			onClickStep && onClickStep(step);
		},
		mouseover: (e) => {
			setHoveredStep(step._id);
			e.target.openPopup();
		},
		mouseout: (e) => {
			setHoveredStep(null);
			e.target.closePopup();
		},
	});

	const handleStepOnMapClick = (step) => {
		trackStepOnMapClick(step);
	};

	useEffect(() => {
		if (selectedStepsIds.length > 0) {
			setSelectedStepId(selectedStepsIds[0]);
		} else if (hoveredStep) {
			setSelectedStepId(hoveredStep?._id);
		} else if (selectedStepId !== steps[0]?._id) {
			setSelectedStepId(steps[0]?._id);
		}
	}, [selectedStepsIds, hoveredStep, steps]);

	const stepsWithMarkers = useMemo(() => {
		return steps.map((step, index) => {
			const isSelected = selectedStepId === step._id;
			let icon;
			let matchedMarker;

			if (markers.length > 0) {
				matchedMarker = markers.find((marker) => marker.key === step.icon);
				if (matchedMarker) {
					const type = isSelected ? GlutenFreeIcons.SELECTED : step.icon;
					icon = createStepIcon(
						{ src: matchedMarker.getSrc(type, index + 1) } || marker
					);
				}
			} else if (isSelected) {
				icon = createStepIcon(stepIcon.selectedStep);
			} else {
				icon = createStepIcon(marker);
			}

			return (
				<Marker
					key={step._id}
					position={[step.lat, step.lon]}
					place={{ ...step, matchedMarker }}
					eventHandlers={markerEventHandlers(step)}
					icon={icon}
					zIndexOffset={isSelected ? 2000 : 500}>
					{(isSelected || showAllTooltips) && (
						<Tooltip
							className="request-popup"
							direction="top"
							permanent={isSelected || showAllTooltips}>
							<div className={classNames(styles.popupTitle, "relative")}>
								{step.title}
							</div>
						</Tooltip>
					)}
				</Marker>
			);
		});
	}, [steps, showAllTooltips, selectedStepId]);

	return (
		<span className={styles.container}>
			<MapContainer
				center={center}
				zoom={zoom || 4}
				zoomControl={false}
				doubleClickZoom={false}
				minZoom={2}
				maxZoom={20}
				scrollWheelZoom={scrollWheelZoom}
				maxBounds={[
					[85, 180],
					[85, -180],
					[-85, -180],
					[-85, 180],
				]}
				style={{
					height: "100%",
					width: "100%",
					position: isMobile && !enforceStaticMap ? "fixed" : "static",
					top: 0,
				}}>
				{children}
				<TileLayer
					maxZoom={20}
					attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
					url={`https://api.mapbox.com/styles/v1/${Config.user_name}/${Config.new_style_id}/tiles/256/{z}/{x}/{y}@2x?access_token=${Config.access_token}`}
				/>
				{isClusterMap ? (
					<MarkerClusterGroup
						// maxClusterRadius={50}
						showCoverageOnHover={false}
						iconCreateFunction={createClusterCustomIcon}>
						{stepsWithMarkers}
					</MarkerClusterGroup>
				) : (
					stepsWithMarkers
				)}
				{withMapEvents ? (
					<MapEvents
						disableZoomEvents={disableZoomEvents}
						flyToPosition={flyToPosition}
						steps={steps}
						isFitBoundsBySteps={isFitBoundsBySteps}
						onClickMap={onClickMap}
						onClickStep={onClickStep}
						onZoomStart={onZoomStart}
						onZoomEnd={onZoomEnd}
						onDragStart={onDragStart}
						onDragEnd={onDragEnd}
						zoomControl={zoomControl}
						updateMapCenter={updateMapCenter}
					/>
				) : null}
			</MapContainer>
		</span>
	);
};

const MapEvents = ({
	flyToPosition,
	steps,
	isFitBoundsBySteps,
	onClickMap,
	onClickStep,
	onZoomStart,
	onZoomEnd,
	onDragStart,
	onDragEnd,
	zoomControl,
	disableZoomEvents,
	updateMapCenter,
}) => {
	const mapRef = useMap();
	const [mapCenterState, setMapCenterState] = useState(null);
	const mapCenterDebounce = useDebounce(mapCenterState, 50);

	useEffect(() => {
		if (mapCenterDebounce && updateMapCenter)
			updateMapCenter({
				position: mapRef.getCenter(),
				zoom: mapRef.getZoom(),
				bounds: mapRef.getBounds(),
			});
	}, [mapCenterDebounce]);

	useEffect(() => {
		if (mapRef && isFitBoundsBySteps) {
			const latslons = steps.map((step) => [step.lat, step.lon]);
			mapRef.fitBounds(latslons, { padding: [10, 10] });
		}
	}, [steps, isFitBoundsBySteps, mapRef]);

	useEffect(() => {
		if (flyToPosition) {
			let isAnimation = true;
			const lat = flyToPosition?.lat || flyToPosition[0];
			const lon = flyToPosition?.lng || flyToPosition?.lon || flyToPosition[1];
			const northEast = mapRef.getBounds().getNorthEast();
			const southWest = mapRef.getBounds().getSouthWest();
			if (
				lat < southWest.lat ||
				lat > northEast.lat ||
				lon < southWest.lng ||
				lon > northEast.lng
			)
				isAnimation = false;

			const currentZoom = mapRef.getZoom();
			mapRef.setView(flyToPosition, currentZoom, {
				animate: isAnimation,
				duration: 1.5,
			});
			setMapCenterState({
				position: mapRef.getCenter(),
				zoom: mapRef.getZoom(),
				bounds: mapRef.getBounds(),
			});
		}
	}, [flyToPosition]);

	useMapEvents({
		click(e) {
			if (Browser.safari) {
				if (!allowClick) return;
				allowClick = false;
				setTimeout(() => (allowClick = true), 200); // handle safari issue (bug on leaflet)
			}
			if (onClickMap && !isStepClicked) {
				onClickMap(e);
			} else if (onClickStep && isStepClicked) {
				onClickStep({
					e,
					center: mapRef.getCenter(),
					bounds: mapRef.getBounds(),
				});
				isStepClicked = false;
			}
		},
		zoomstart(e) {
			if (disableZoomEvents) return;
			if (onZoomStart) onZoomStart(e);
		},
		zoomend(e) {
			if (disableZoomEvents) return;
			setMapCenterState({
				position: mapRef.getCenter(),
				zoom: mapRef.getZoom(),
				bounds: mapRef.getBounds(),
			});
			if (onZoomEnd)
				onZoomEnd({
					e,
					bounds: mapRef.getBounds(),
					center: mapRef.getCenter(),
				});
		},
		dragend(e) {
			setMapCenterState({
				position: mapRef.getCenter(),
				zoom: mapRef.getZoom(),
				bounds: mapRef.getBounds(),
			});
			if (onDragEnd)
				onDragEnd({
					e,
					bounds: mapRef.getBounds(),
					center: mapRef.getCenter(),
				});
		},
		dragstart(e) {
			if (onDragStart)
				onDragStart({
					e,
					bounds: mapRef.getBounds(),
					center: mapRef.getCenter(),
				});
		},
	});

	return (
		<>
			{zoomControl && (
				<div className={styles.zoomPanel}>
					<button
						className={styles.zoomControl}
						onClick={() => {
							mapRef.setZoom(mapRef.getZoom() + 1);
						}}>
						<img src="/images/top-ten/plus.svg" />
					</button>
					<button
						className={styles.zoomControl}
						onClick={() => {
							mapRef.setZoom(mapRef.getZoom() - 1);
						}}>
						<img src="/images/top-ten/minus.svg" />
					</button>
				</div>
			)}
		</>
	);
};

export default MapSTPS;
